import React, { useReducer, useEffect } from 'react';
import api from '../../utils/api';
import AuthContext from './authContext';
import authReducer from './authReducer';
import setAuthToken from '../../utils/setAuthToken';
import { hasLocalData } from '../../utils/localStorageUtils';
import {
  REGISTER_SUCCESS,
  REGISTER_FAIL,
  USER_LOADED,
  AUTH_ERROR,
  LOGIN_SUCCESS,
  LOGIN_FAIL,
  LOGOUT,
  CLEAR_ERRORS,
  UPDATE_PROFILE_SUCCESS,
  SET_USER_ACTIVE_DECK,
  SET_USER_ACTIVE_TEAM,
  SET_DARK_MODE
} from '../types';

const AuthState = props => {
  const initialState = {
    token: localStorage.getItem('token'),
    isAuthenticated: null,
    loading: true,
    user: null,
    error: null
  };

  const [state, dispatch] = useReducer(authReducer, initialState);
  
  // Callback for when login is successful (to trigger local match sync)
  let onLoginSuccessCallback = null;

  // Load User
  const loadUser = async () => {

    // Normal flow for production
    if (localStorage.token) {
      setAuthToken(localStorage.token);
    }

    try {
      const res = await api.get('/api/auth/user');

      dispatch({
        type: USER_LOADED,
        payload: res.data
      });
    } catch (err) {
      console.error('Error loading user:', err.message);
      dispatch({ type: AUTH_ERROR });
    }
  };

  // Load user on component mount if token exists
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
      loadUser();
    } else {
      // No token, set loading to false and isAuthenticated to false
      dispatch({ type: AUTH_ERROR });
    }
  }, []);

  // Register User
  const register = async formData => {

    try {
      const res = await api.post('/api/auth/register', formData);

      // Set auth token in headers
      if (res.data && res.data.token) {
        setAuthToken(res.data.token);
      }

      dispatch({
        type: REGISTER_SUCCESS,
        payload: res.data
      });

      // Check for local matches and trigger sync if callback is set
      if (hasLocalData() && onLoginSuccessCallback) {
        console.log('Local matches found after registration, triggering sync...');
        onLoginSuccessCallback();
      }

      // No need to call loadUser() since we have the user data in response
    } catch (err) {
      console.error('Registration error:', err.message);
      dispatch({
        type: REGISTER_FAIL,
        payload: err.response?.data?.message || 'Registration failed'
      });
    }
  };

  // Login User
  const login = async formData => {
    try {
      console.log('Attempting login with credentials');
      
      // Add a timeout indicator to provide better feedback
      const loginTimeout = setTimeout(() => {
        console.log('Login request is taking longer than expected...');
      }, 5000);
      
      const res = await api.post('/api/auth/login', formData);
      
      // Clear the timeout
      clearTimeout(loginTimeout);
      console.log('Login successful, received token and user data');

      // Set auth token in headers
      if (res.data && res.data.token) {
        setAuthToken(res.data.token);
      }

      dispatch({
        type: LOGIN_SUCCESS,
        payload: res.data
      });

      // Check for local matches and trigger sync if callback is set
      if (hasLocalData() && onLoginSuccessCallback) {
        console.log('Local matches found, triggering sync after state update...');
        // Use setTimeout to ensure state has been updated before triggering sync
        setTimeout(() => {
          onLoginSuccessCallback();
        }, 50);
      }

      // No need to call loadUser() since we have the user data in response
    } catch (err) {
      console.error('Login error:', err);
      
      // Enhanced error logging for debugging
      if (err.response) {
        console.error('Server response error:', {
          status: err.response.status,
          data: err.response.data,
          headers: err.response.headers
        });
      } else if (err.request) {
        console.error('No response received from server:', err.request);
      } else {
        console.error('Request setup error:', err.message);
      }

      dispatch({
        type: LOGIN_FAIL,
        payload: err.response?.data?.message || 'Login failed. Please try again later.'
      });
    }
  };

  // Logout
  const logout = () => dispatch({ type: LOGOUT });

  // Update Profile
  const updateProfile = async (profileData) => {
    console.log('Updating profile with data:', profileData);
    
    // Save gameMode to localStorage to persist the setting
    if (profileData.gameMode) {
      localStorage.setItem('gameMode', profileData.gameMode);
    }
    
    try {
      // Extract player profile fields
      const { playerName, playerId, birthdate, division, format, gameMode, name, profilePicture } = profileData;
      
      // Prepare complete profile update data including name and profilePicture
      const updateData = {
        playerName,
        playerId,
        birthdate,
        division,
        format,
        gameMode,
        name,
        profilePicture
      };
      
      // Send complete profile data including profilePicture field
      const res = await api.put('/api/auth/profile', updateData);
      console.log('Profile update success:', res.data);
      
      dispatch({
        type: UPDATE_PROFILE_SUCCESS,
        payload: res.data
      });

      return res.data;
    } catch (err) {
      console.error('Profile update error:', err);
      console.error('Error updating profile:', err.response?.data?.message || 'Update failed');
      throw err; // Re-throw the error to be caught by the caller
    }
  };

  // Set active deck
  const setActiveDeck = async (deckId) => {
    try {
      const res = await api.put('/api/auth/active-deck', { deckId });
      
      dispatch({
        type: SET_USER_ACTIVE_DECK,
        payload: res.data
      });
      
      return res.data;
    } catch (err) {
      console.error('Error setting active deck:', err);
      throw err;
    }
  };
  
  // Set active team
  const setActiveTeam = async (teamId) => {
    try {
      const res = await api.put('/api/auth/active-team', { teamId });
      
      dispatch({
        type: SET_USER_ACTIVE_TEAM,
        payload: res.data
      });
      
      return res.data;
    } catch (err) {
      console.error('Error setting active team:', err);
      throw err;
    }
  };

  // Set dark mode preference
  const setDarkMode = async (darkModeEnabled) => {
    try {
      const res = await api.put('/api/auth/dark-mode', { darkModeEnabled });
      
      dispatch({
        type: SET_DARK_MODE,
        payload: res.data
      });
      
      return res.data;
    } catch (err) {
      console.error('Error setting dark mode preference:', err);
      throw err;
    }
  };

  // Update user with new fields (like ptcglUsername)
  const updateUser = async (userData) => {
    try {
      const res = await api.put('/api/auth/update-user', userData);
      
      dispatch({
        type: USER_LOADED,
        payload: res.data
      });
      
      return res.data;
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  // Clear Errors
  const clearErrors = () => dispatch({ type: CLEAR_ERRORS });

  // Set the callback for when login is successful
  const setOnLoginSuccessCallback = (callback) => {
    onLoginSuccessCallback = callback;
  };

  return (
    <AuthContext.Provider
      value={{
        token: state.token,
        isAuthenticated: state.isAuthenticated,
        loading: state.loading,
        user: state.user,
        error: state.error,
        register,
        loadUser,
        login,
        logout,
        clearErrors,
        updateProfile,
        setActiveDeck,
        setActiveTeam,
        setDarkMode,
        updateUser,
        setOnLoginSuccessCallback
      }}
    >
      {props.children}
    </AuthContext.Provider>
  );
};

export default AuthState;
